import { useAppSelector } from '@/store';
import { selectFileCloneStatusMap, selectFileCloneErrorMap } from '@/store/selectors/poc/cloneStatusSelectors';
import { selectUser } from '@/store/selectors/user';
import { userHasFileKey } from '@/utils/fileAccess.utils';
import type { FileCloneStatus, FileMetadata } from '@/types/poc/helia';

/**
 * Returns the clone status for a specific file CID, with 'no-access'
 * overlay when the current user lacks decryption keys.
 *
 * Each selector returns a primitive (string), so useAppSelector's
 * default === comparison prevents re-renders when other CIDs change.
 */
export function useFileCloneStatus(cid: string, file: FileMetadata): FileCloneStatus {
  const rawStatus: FileCloneStatus = useAppSelector(
    state => selectFileCloneStatusMap(state)[cid] ?? 'unknown'
  );
  const userId = useAppSelector(state => selectUser(state)?.id);

  if (file.type !== 'folder' && userId !== undefined && !userHasFileKey(file, userId)) {
    return 'no-access';
  }

  return rawStatus;
}

/**
 * Returns the clone error message for a specific CID, or undefined.
 */
export function useFileCloneError(cid: string): string | undefined {
  return useAppSelector(state => selectFileCloneErrorMap(state)[cid]);
}
